import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthActions } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { getUserFriendlyAuthError } from '@/utils/authErrors';
import Button from '@/components/Common/Button';
import styles from './LoginPage.module.css';

/**
 * LoginPage Component
 * User authentication form
 */
const LoginPage: FC = () => {
  const navigate = useNavigate();
  const { login } = useAuthActions();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(formData);
      navigate('/dashboard');
    } catch (err) {
      // Get user-friendly error message with translation support
      const translationKey = getUserFriendlyAuthError(err, true);
      const fallbackMessage = err instanceof Error ? err.message : t('auth.invalidCredentials');
      
      // Try to use translated message, fallback to error message if translation key doesn't exist
      setError(t(translationKey) || fallbackMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>ALIANZO</h1>
          <p className={styles.subtitle}>Portal de Acesso</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              {t('auth.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={styles.input}
              placeholder="seu.email@empresa.com"
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              {t('auth.password')}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={styles.input}
              placeholder="••••••••"
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.rememberForgot}>
            <div className={styles.checkboxField}>
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                className={styles.checkbox}
                disabled={isLoading}
              />
              <label htmlFor="rememberMe" className={styles.checkboxLabel}>
                Lembrar-me
              </label>
            </div>
            <a href="#" className={styles.forgotLink}>
              Esqueceu a senha?
            </a>
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            fullWidth
          >
            ACESSAR
          </Button>
        </form>

        <div className={styles.signupLink}>
          Não tem conta? <a href="#">Solicite acesso</a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;